<template>
  <section class="gate-widget" :class="{ '--member-post': isMemberPost }">
    <div class="gate-widget__logo">
      <img :src="require('./logo-bull.svg')" width="24" height="24" alt="Bravado Logo" />
    </div>
    <BravadoText tag="h2" variant="header3">
      <slot>
        <span>Join the community built by salespeople, for salespeople</span>
      </slot>
    </BravadoText>

    <SignUpSocialButtons
      v-if="!isUserInsideExternalApp"
      class="gate-widget__buttons"
      :login-params="{ auth_source: 'gate-widget' }"
      @loginWithLinkedin="loginWithLinkedin"
      @loginWithGoogle="loginWithGoogle"
    />
    <BravadoButtonV3
      v-else
      class="gate-widget__buttons gate-widget__sign-up-button"
      variant="blank"
      full-width
      @click="navigateToSignUp"
    >
      Become a Member
    </BravadoButtonV3>

    <SignUpFooter class="gate-widget__footer" @navigateToSignIn="navigateToSignIn" />
  </section>
</template>

<script lang="ts" setup>
import { onMounted, ref, useContext, useRoute } from '@nuxtjs/composition-api';
import { buildOAuthUrl } from '@/components/common/SignUpModal/utils';
import SignUpSocialButtons from '@/components/common/SignUpModal/SignUpSocialButtons/SignUpSocialButtons.vue';
import SignUpFooter from '@/components/common/SignUpModal/SignUpFooter/SignUpFooter.vue';
import BravadoButtonV3 from '@/components/common/BravadoButtonV3/BravadoButtonV3.vue';
import { isUserInsideExternalApp as isUserInsideExternalAppUtil } from '@/utils/externalRedirections';

type WarRoomGateWidgetProps = {
  // Title supports html, because we need to add line break sometimes
  title?: string;
  // Gate in memeber post has different styles
  // - fading on background
  // - overlay the comment above
  isMemberPost?: boolean;
};

const props = withDefaults(defineProps<WarRoomGateWidgetProps>(), {
  title: 'Join the community built by salespeople, for salespeople',
  isMemberPost: false,
});

const { $device, $analytics, $modal } = useContext();
const route = useRoute();

const isUserInsideExternalApp = ref(false);

onMounted(() => {
  isUserInsideExternalApp.value = isUserInsideExternalAppUtil(
    document.referrer,
    navigator.userAgent,
    $device.isWebView,
  );
});

function loginWithLinkedin() {
  $analytics?.logEvent('war-room__gate-widget__linkedin-button__click', {
    memberPost: props.isMemberPost,
  });
  window.location.assign(buildOAuthUrl('linkedin', 'signUp', route));
}

function loginWithGoogle() {
  $analytics?.logEvent('war-room__gate-widget__google-button__click', {
    memberPost: props.isMemberPost,
  });
  window.location.assign(buildOAuthUrl('google', 'signUp', route));
}

function navigateToSignIn() {
  $analytics?.logEvent('war-room__gate-widget__login-link__click', {
    memberPost: props.isMemberPost,
  });
  $modal.showWithRoute('SIGN_IN_MODAL');
}

function navigateToSignUp() {
  $modal.showWithRoute('SIGN_UP_MODAL');
}
</script>

<style lang="scss">
.gate-widget {
  display: flex;
  flex-direction: column;
  padding: 40px 16px 40px;
  margin: 28px -16px -28px;
  background: var(--theme-color-over-card);
  color: var(--theme-color-text);
  text-align: center;
  align-items: center;

  @include mq('sm') {
    padding: 40px 24px 40px;
    margin: 28px -24px -28px;
    border-bottom-left-radius: 8px;
    border-bottom-right-radius: 8px;
  }

  &__logo {
    margin: 0 auto 8px;
    width: 56px;
    height: 56px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: var(--black);
    border-radius: 50%;

    .bravado-theme--dark & {
      background: var(--white);
    }

    img {
      filter: var(--theme-image-invert);
    }
  }

  h2 {
    max-width: 410px;
    margin-bottom: 16px;
  }

  &__buttons {
    max-width: 270px;
  }

  &__sign-up-button {
    // Modify the "blank" button style to match the design
    &.bravado-button-v3--blank {
      background-color: var(--white);
      box-shadow: var(--theme-elevation-01);
      color: var(--black);
      font-weight: 600;

      &:hover,
      &:focus {
        background-color: var(--gray-10) !important;
      }
    }
  }

  &.--member-post {
    position: relative;
    z-index: 1;
    margin-top: -200px;
    padding-top: 100px;
    // We must use rgb version for color here, not hex

    background: linear-gradient(
      180deg,
      rgba(var(--theme-over_card-rgb), 0) 0%,
      rgba(var(--theme-over_card-rgb), 1) 130px
    );
  }
}
</style>
